import axios from "axios";
import { API_BASE_URL, API_HEADERS } from "../apiConfig";
import { API_ENDPOINTS } from "../apiEndpoints";

/**
 * Get user address list for a customer. Use default address (IsDefault === true) for Ship to.
 * GET useraddress/List?CustomerID={customerId} with Token, UserName.
 * @param {string|number} customerId - Customer ID (from login userData.customerId).
 * @param {string} userName - Logged-in user's email (UserName header).
 * @returns {Promise<Array>} List of addresses [{ ShippingAddressID, RecipientName, StreetAddress, City, State, PostalCode, Country, PhoneNumber, IsDefault }, ...]
 */
export const getUserAddressList = async (customerId, userName) => {
  const url = `${API_BASE_URL}/${API_ENDPOINTS.GET_UserAddressList}?CustomerID=${encodeURIComponent(customerId)}`;
  const response = await axios.get(url, {
    headers: {
      Token: API_HEADERS.Token,
      UserName: userName || API_HEADERS.UserName,
    },
  });
  return Array.isArray(response.data) ? response.data : [];
};

/**
 * Get a single user address by ShippingAddressID. Used when editing Ship to (Change modal).
 * GET useraddress/Get?id={ShippingAddressID} with Token, UserName.
 * @param {string|number} shippingAddressId - ShippingAddressID from list response.
 * @param {string} userName - Logged-in user's email (UserName header).
 * @returns {Promise<object>} Address object { ShippingAddressID, CustomerId, RecipientName, StreetAddress, City, State, PostalCode, Country, PhoneNumber, IsDefault }
 */
export const getUserAddress = async (shippingAddressId, userName) => {
  const url = `${API_BASE_URL}/${API_ENDPOINTS.GET_UserAddressGet}?id=${encodeURIComponent(shippingAddressId)}`;
  const response = await axios.get(url, {
    headers: {
      Token: API_HEADERS.Token,
      UserName: userName || API_HEADERS.UserName,
    },
  });
  return response.data;
};

/**
 * Update a user address. PUT/POST useraddress/Put with JSON body.
 * @param {object} payload - { ShippingAddressID, CustomerId, RecipientName, StreetAddress, City, State, PostalCode, Country, PhoneNumber, IsDefault }
 * @param {string} userName - Logged-in user's email (UserName header).
 * @returns {Promise<object>} Response e.g. { success: 1, msg: "Record save Successfully" }
 */
export const updateUserAddress = async (payload, userName) => {
  const url = `${API_BASE_URL}/${API_ENDPOINTS.POST_UserAddressPut}`;
  const response = await axios.request({
    method: "post",
    url,
    data: payload,
    headers: {
      Token: API_HEADERS.Token,
      UserName: userName || API_HEADERS.UserName,
      "Content-Type": "application/json",
    },
  });
  return response.data;
};

/**
 * Get user profile from API.
 * Uses Token from config; UserName is the logged-in user's email/username.
 * @param {string} userName - Logged-in user's username (e.g. email). If not provided, uses API_HEADERS.UserName.
 * @returns {Promise<object>} User profile response (FirstName, LastName, PhoneNumber, BusinessName, BusinessStreetAddress, etc.)
 */
export const getUserProfile = async (userName) => {
  const url = `${API_BASE_URL}/${API_ENDPOINTS.GET_UserProfile}`;
  const response = await axios.get(url, {
    headers: {
      Token: API_HEADERS.Token,
      UserName: userName || API_HEADERS.UserName,
    },
  });
  return response.data;
};

/**
 * Update user profile (My Account → My Profile → Save Changes).
 * POST Accounts/UserProfile with Token + UserName (logged-in email) headers.
 * Body matches API: CustomerID, FirstName, LastName, Country, UserName (null), Email, PhoneNumber,
 * LegalEntityName, BusinessName, BusinessCategory, business fields, EIN, etc.
 * @param {object} payload - Full profile object as required by API.
 * @param {string} userName - Logged-in user's email (UserName header).
 * @returns {Promise<object>} Updated profile (same shape as GET) or API message object.
 */
export const updateUserProfile = async (payload, userName) => {
  const url = `${API_BASE_URL}/${API_ENDPOINTS.POST_UserProfile}`;
  const response = await axios.post(url, payload, {
    headers: {
      Token: API_HEADERS.Token,
      UserName: userName || API_HEADERS.UserName,
      "Content-Type": "application/json",
    },
  });
  return response.data;
};

/**
 * Trigger reset password / get token for password change (Security section).
 * GET accounts/resetpassword?Username={email} with Token: "erp.gold", UserName: logged-in user email.
 * @param {string} userName - Logged-in user's email.
 * @returns {Promise<object>} Response with UserID, UserName, Token, NewPassword, ConfirmNewPassword (Token used for update password API later).
 */
export const getResetPasswordToken = async (userName) => {
  const url = `${API_BASE_URL}/${API_ENDPOINTS.GET_ResetPassword}?Username=${encodeURIComponent(userName)}`;
  const response = await axios.get(url, {
    headers: {
      Token: "erp.gold",
      UserName: userName,
    },
  });
  return response.data;
};

/**
 * Forgot password (public login flow).
 * POST Accounts/ForgetPassword with Token + UserName headers (email in textbox).
 * Body: { UserName: email }
 * @param {string} userName - Email entered on forgot-password form.
 * @returns {Promise<object>} e.g. { success: 1, msg: "Email Send Successfully" }
 */
export const forgetPassword = async (userName) => {
  const trimmed = (userName || "").trim();
  const url = `${API_BASE_URL}/${API_ENDPOINTS.POST_ForgetPassword}`;
  const response = await axios.post(
    url,
    { UserName: trimmed },
    {
      headers: {
        Token: API_HEADERS.Token,
        UserName: trimmed,
        "Content-Type": "application/json",
      },
    },
  );
  return response.data;
};

/**
 * Resend account confirmation email (logged-in user, email not verified).
 * POST Accounts/ResendConfirmationEmail — headers only: Token, UserName (user email).
 * @param {string} userName - Logged-in user's email (UserName header).
 * @returns {Promise<object>} API response e.g. { success: 1, msg: "..." }
 */
export const resendConfirmationEmail = async (userName) => {
  const trimmed = (userName || "").trim();
  const url = `${API_BASE_URL}/${API_ENDPOINTS.POST_ResendConfirmationEmail}`;
  const response = await axios.post(
    url,
    {},
    {
      headers: {
        Token: API_HEADERS.Token,
        UserName: trimmed,
        "Content-Type": "application/json",
      },
    },
  );
  return response.data;
};

/**
 * Confirm email from verification link.
 * POST Accounts/Confirm with headers Token + UserName and body { UserName, Token }.
 * @param {string} userName - Email from confirm URL.
 * @param {string} token - Confirmation token from confirm URL.
 * @returns {Promise<object>} API response with success/msg.
 */
export const confirmEmail = async (userName, token) => {
  const emailTrimmed = (userName || "").trim();
  const tokenValue = (token || "").trim();
  const url = `${API_BASE_URL}/${API_ENDPOINTS.POST_Confirm}`;
  const response = await axios.post(
    url,
    {
      UserName: emailTrimmed,
      Token: tokenValue,
    },
    {
      headers: {
        Token: API_HEADERS.Token,
        UserName: emailTrimmed,
        "Content-Type": "application/json",
      },
    },
  );
  return response.data;
};

/**
 * Update / reset password (Security section).
 * POST Accounts/ResetPassword with Token: "erp.gold", UserName: logged-in user email.
 * Body: { UserID, UserName, Token, NewPassword, ConfirmNewPassword } (Token from getResetPasswordToken response).
 * @param {object} payload - { UserID, UserName, Token, NewPassword, ConfirmNewPassword }
 * @param {string} userName - Logged-in user's email (for UserName header).
 * @returns {Promise<object>} Response e.g. { success: 1, msg: "Password Change Successfully" }
 */
export const resetPassword = async (payload, userName) => {
  const url = `${API_BASE_URL}/${API_ENDPOINTS.POST_ResetPassword}`;
  const response = await axios.post(url, payload, {
    headers: {
      Token: "erp.gold",
      UserName: userName,
      "Content-Type": "application/json",
    },
  });
  return response.data;
};

export const userProfileService = {
  getUserProfile,
  updateUserProfile,
  getUserAddressList,
  getUserAddress,
  updateUserAddress,
  getResetPasswordToken,
  forgetPassword,
  resendConfirmationEmail,
  confirmEmail,
  resetPassword,
};
